// src/components/Informascota.jsx
import petfamLogo from "../assets/PetfamLogo.png";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, Pencil, ArrowRight, Check, Plus, Trash2, ShieldCheck, Syringe } from "lucide-react";
import Cropper from "react-easy-crop";
import { authFetch } from "../utils/authFetch";
import petfamBrand from "../assets/Petfamp.png";
import medismartBrand from "../assets/medismart.png";

import "../styles/informascota.css";

import etImg from "../assets/Et.webp";
import golden from "../assets/golden.webp";
import { getBreedImage } from "../config/petBreedImages";

import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import config from "../config";
import { createPortal } from "react-dom";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

// ✅ URL QA Medismart (correo)
const MEDISMART_QA_URL = "https://petfam.medismart.live";

const carnetInputStyle = {
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  minWidth: 0,
  height: 58,
  borderRadius: 18,
  border: "1px solid #d7dee2",
  background: "#fff",
  padding: "0 24px",
  fontFamily: "Poppins",
  fontSize: 16,
  fontWeight: 500,
  color: "#0B2F3E",
  outline: "none",
  WebkitAppearance: "auto",
  appearance: "auto",
};

// =======================================================
// CALCULAR EDAD
// =======================================================
function getAgeLabel(birthDateStr) {
  if (!birthDateStr) return "Edad no registrada";

  const s = String(birthDateStr).trim().slice(0, 10);
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return "Edad no registrada";

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  // ✅ crear fecha LOCAL, no UTC
  const birthDate = new Date(year, month - 1, day);

  if (
    isNaN(birthDate.getTime()) ||
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() !== month - 1 ||
    birthDate.getDate() !== day
  ) {
    return "Edad no registrada";
  }

  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (days < 0) {
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
    months -= 1;
  }

  if (months < 0) {
    months += 12;
    years -= 1;
  }

  if (years < 0) return "Fecha inválida";

  if (years === 0 && months === 0) {
    if (days <= 0) return "Menos de 1 día";
    return days === 1 ? "1 día" : `${days} días`;
  }

  if (years === 0) return `${months} ${months === 1 ? "mes" : "meses"}`;
  if (months === 0) return `${years} ${years === 1 ? "año" : "años"}`;

  return `${years} ${years === 1 ? "año" : "años"} y ${months} ${
    months === 1 ? "mes" : "meses"
  }`;
}

// =======================================================
// OBTENER ID DEL USUARIO
// =======================================================
function getCurrentUserId() {
  try {
    const keys = ["petfamUser", "user", "userData", "loginUser", "idUser"];
    for (const k of keys) {
      const raw = localStorage.getItem(k);
      if (!raw) continue;

      if (k === "idUser") {
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      }

      const parsed = JSON.parse(raw);
      if (parsed.idUser) return parsed.idUser;
      if (parsed.id) return parsed.id;
      if (parsed.userId) return parsed.userId;
    }
  } catch {}
  return null;
}

// =======================================================
// ✅ PERSISTIR MASCOTA ACTIVA (para volver desde Medismart)
// =======================================================
function getLastPetIdKey(idUser) {
  return idUser ? `petfam:lastPetId:${idUser}` : "petfam:lastPetId";
}

function saveLastPetId(idUser, idPet) {
  const n = Number(idPet);
  if (!Number.isFinite(n) || n <= 0) return; // ✅ solo ids válidos > 0
  try {
    localStorage.setItem(getLastPetIdKey(idUser), String(n));
    localStorage.setItem(`${getLastPetIdKey(idUser)}:ts`, String(Date.now()));
  } catch {}
}

function readLastPetId(idUser) {
  try {
    const raw = localStorage.getItem(getLastPetIdKey(idUser));
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null; // ✅ 0 se vuelve null
  } catch {
    return null;
  }
}

function toISODateOnly(v) {
  if (!v) return "";
  const s = String(v);
  if (s.includes("T")) return s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  try {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  } catch {}
  return "";
}

const normStatus = (s) => String(s || "").trim().toUpperCase();
const isDoneStatus = (s) => normStatus(s) === "HECHO" || normStatus(s) === "DONE";

const makeRowId = () =>
  `ROW-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function upper(v) {
  return String(v || "").toUpperCase();
}

function getPetSpeciesForVaccines(pet) {
  const raw = String(
    pet?.species ||
      pet?.tipo ||
      pet?.type ||
      pet?.animalType ||
      ""
  ).toLowerCase();

  return raw.includes("gat") ? "GATO" : "PERRO";
}

// =======================================================
// URL -> BASE64 (para poder meter imágenes en jsPDF)
// =======================================================
async function imageUrlToBase64(url) {
  if (!url) throw new Error("URL vacía");

  if (String(url).startsWith("data:image/")) return url;

  if (String(url).startsWith("blob:")) {
    const res = await fetch(url);
    if (!res.ok) throw new Error("No se pudo cargar la imagen (blob)");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  let finalUrl = String(url);
  if (finalUrl.startsWith("/uploads/")) {
    finalUrl = `${API_BASE_URL}${finalUrl}`;
  } else if (finalUrl.startsWith("uploads/")) {
    finalUrl = `${API_BASE_URL}/${finalUrl}`;
  }

  const res = await fetch(finalUrl, { mode: "cors" });
  if (!res.ok) throw new Error(`No se pudo cargar la imagen (${res.status})`);

  const blob = await res.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ===============================
// ✅ CELDA EDITABLE (BD) - Veterinario
// ===============================
function VeterinarianCell({
  API_BASE_URL,
  rowId,
  bdEventId,
  initialValue,
  disabled,
  onSaved,
}) {
  const [val, setVal] = React.useState(initialValue || "");
  const tRef = React.useRef(null);

  React.useEffect(() => {
    setVal(initialValue || "");
  }, [initialValue, bdEventId]);

  const save = async (nextVal) => {
    if (!bdEventId) return;

    try {
      const res = await authFetch(`/calendar/events/${bdEventId}/veterinarian`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ veterinarian: nextVal || "" }),
      });

      if (!res.ok) throw new Error(`Backend respondió ${res.status}`);
      onSaved?.(rowId, nextVal || "");
    } catch (e) {
      console.error(e);
      // ✅ No quitamos foco ni borramos texto
    }
  };

  const handleChange = (e) => {
    const nextVal = e.target.value;
    setVal(nextVal);

    if (tRef.current) window.clearTimeout(tRef.current);
    tRef.current = window.setTimeout(() => save(nextVal), 600);
  };

  return (
    <input
      className="nav-input"
      style={{ height: 42, flex: 1 }}
      value={val}
      onChange={handleChange}
      disabled={disabled}
      placeholder="Nombre del veterinario"
      title="Se guarda automáticamente en la BD"
    />
  );
}

// ===============================
// ✅ CARNET - COMPONENTE FUERA (NO SE DESMONTA)
// ===============================
const CarnetContent = React.memo(function CarnetContent({
  API_BASE_URL,

  // estado
  carnetRows,
  loadingCarnet,
  carnetError,
  savingCarnet,

  // acciones carnet
  addCarnetRow,
  updateCarnetField,
  saveNewCarnetRow,
  removeCarnetRow,
deleteCarnetRowFromBackend,
deleteMode,
toggleDeleteMode,
setCarnetRows,

  // QR / PDF
  loadingPublicId,
  carnetPublicUrl,
  downloadCarnetPdf,
}) {
  return (
    <>
      {/* ✅ Header con + Añadir */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h3 className="vacc-title" style={{ margin: 15 }}>
          Carnet de vacunación
        </h3>

        <div style={{ display: "flex", gap: 8, marginRight: 28 }}>
  <button
    type="button"
    onClick={toggleDeleteMode}
    disabled={savingCarnet || carnetRows.length === 0}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "none",
      cursor: "pointer",
      padding: "5px 14px",
      borderRadius: 999,
      background: deleteMode ? "#C0392B" : "#fff",
      color: deleteMode ? "#fff" : "#C0392B",
      fontFamily: "Poppins",
      fontWeight: 600,
      boxShadow: "0 10px 24px rgba(0,0,0,.12)",
      whiteSpace: "nowrap",
      opacity: savingCarnet || carnetRows.length === 0 ? 0.6 : 1,
    }}
  >
    <Trash2 size={16} />
    Eliminar
  </button>

  <button
    type="button"
    onClick={addCarnetRow}
    disabled={savingCarnet}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "none",
      cursor: "pointer",
      padding: "5px 14px",
      borderRadius: 999,
      background: "#0F6B7A",
      color: "#fff",
      fontFamily: "Poppins",
      fontWeight: 600,
      boxShadow: "0 10px 24px rgba(0,0,0,.12)",
      whiteSpace: "nowrap",
      opacity: savingCarnet ? 0.6 : 1,
    }}
    title="Añadir vacuna en este carnet"
  >
    <Plus size={16} />
    Añadir
  </button>
</div>
      </div>

      {loadingCarnet && (
        <p className="pf-cal-event-desc" style={{ marginTop: 10 }}>
          Cargando carnet…
        </p>
      )}

      {carnetError && (
        <p className="pf-cal-event-desc" style={{ marginTop: 10, color: "#c0392b" }}>
          {carnetError}
        </p>
      )}

      <div className="vacc-table-wrap">
        <div className="vacc-table" style={{ marginTop: 12 }}>
          <div
  className="vacc-header"
  style={{
    gridTemplateColumns: deleteMode
      ? "44px 160px 1.6fr 1.2fr"
      : "160px 1.6fr 1.2fr",
  }}
>
  {deleteMode && <span></span>}
            <span>Fecha</span>
            <span>Vacuna</span>
            <span>Veterinario</span>
          </div>

          {carnetRows.length === 0 ? (
            <div className="vacc-row" style={{ gridTemplateColumns: "1fr", opacity: 0.85 }}>
              <span>
                Aún no tienes vacunas en <strong>HECHO</strong> para esta mascota. Márcalas como
                aplicada desde el calendario.
              </span>
            </div>
          ) : (
            carnetRows.map((row) => {
              const isNew = !!row._isNew;

              return (
                <div
                  className="vacc-row"
                  key={row.id}
                  style={{
  gridTemplateColumns: deleteMode ? "44px 160px 1.6fr 1.2fr" : "160px 1.6fr 1.2fr",
  gap: 12,
  alignItems: "center",
}}
                >
                  {deleteMode && (
  <button
    type="button"
    onClick={() => deleteCarnetRowFromBackend(row)}
    disabled={savingCarnet}
    title="Eliminar vacuna"
    style={{
      width: 38,
      height: 38,
      borderRadius: 999,
      border: "none",
      background: "#C0392B",
      color: "#fff",
      display: "grid",
      placeItems: "center",
      cursor: "pointer",
      opacity: savingCarnet ? 0.6 : 1,
    }}
  >
    <Trash2 size={17} />
  </button>
)}
                  
                  {/* FECHA */}
                  <input
                    className="nav-input"
                    style={{ height: 42 }}
                    type="date"
                    value={row.date || ""}
                    disabled={!isNew}
                    onChange={(e) => updateCarnetField(row.id, "date", e.target.value)}
                    title={isNew ? "Fecha" : "Fecha (desde BD)"}
                  />

                  {/* VACUNA */}
                  <input
                    className="nav-input"
                    style={{ height: 42 }}
                    value={row.vaccine || ""}
                    disabled={!isNew}
                    onChange={(e) => updateCarnetField(row.id, "vaccine", e.target.value)}
                    placeholder={isNew ? "Nombre de la vacuna" : ""}
                    title={isNew ? "Vacuna" : "Vacuna (desde BD)"}
                  />

                  {/* VETERINARIO + acciones */}
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    {isNew ? (
                      <input
                        className="nav-input"
                        style={{ height: 42, flex: 1 }}
                        value={row.veterinarian || ""}
                        onChange={(e) => updateCarnetField(row.id, "veterinarian", e.target.value)}
                        placeholder="Nombre del veterinario"
                        title="Veterinario"
                      />
                    ) : (
                      <VeterinarianCell
                        API_BASE_URL={API_BASE_URL}
                        rowId={row.id}
                        bdEventId={row._bdEventId}
                        initialValue={row.veterinarian || ""}
                        disabled={false}
                        onSaved={(rid, v) => {
                          // ✅ sincroniza carnetRows UNA SOLA VEZ cuando el backend guarda
                          setCarnetRows((prev) =>
                            (Array.isArray(prev) ? prev : []).map((r) =>
                              r.id === rid ? { ...r, veterinarian: v } : r
                            )
                          );
                        }}
                      />
                    )}

                    {isNew && (
                      <>
                        <button
                          type="button"
                          onClick={() => saveNewCarnetRow(row)}
                          disabled={savingCarnet}
                          style={{
                            height: 42,
                            padding: "0 14px",
                            borderRadius: 999,
                            border: "none",
                            cursor: "pointer",
                            background: "#0F6B7A",
                            color: "#fff",
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            opacity: savingCarnet ? 0.6 : 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Guardar
                        </button>

                        <button
                          type="button"
                          onClick={() => removeCarnetRow(row.id)}
                          disabled={savingCarnet}
                          style={{
                            height: 42,
                            padding: "0 12px",
                            borderRadius: 999,
                            border: "1px solid rgba(11,47,62,.2)",
                            cursor: "pointer",
                            background: "#fff",
                            color: "#0B2F3E",
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Quitar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <p className="pf-cal-event-desc" style={{ marginTop: 12, opacity: 0.75 }}>
        * Recuerda que este carnet <strong>no</strong> reemplaza un carnet en físico pero te ayuda a
        llevar un control de tu mascota.
      </p>

      <div className="carnet-actions">
        <div className="carnet-qr">
          {loadingPublicId ? (
            <span style={{ fontSize: 12, opacity: 0.7 }}>Generando QR…</span>
          ) : carnetPublicUrl ? (
            <>
              <QRCodeCanvas value={carnetPublicUrl} size={88} includeMargin />
              <div className="carnet-qr-text">
                <div className="carnet-qr-title">QR único de esta mascota</div>
                <div className="carnet-qr-sub">Al escanearlo, se verá solo este carnet.</div>
              </div>
            </>
          ) : (
            <span style={{ fontSize: 12, opacity: 0.7 }}>No se pudo generar el QR.</span>
          )}
        </div>

        <button type="button" className="carnet-pdf-btn" onClick={downloadCarnetPdf}>
          Descargar PDF
        </button>
      </div>
    </>
  );
});

function CarnetDateField({ value, onChange, placeholder = "Fecha" }) {
  const inputRef = React.useRef(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;

    try {
      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.focus();
        input.click();
      }
    } catch {
      input.focus();
      input.click();
    }
  };

  return (
    <div
      className="carnet-date-field"
      onClick={openPicker}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") openPicker();
      }}
    >
      <span className={value ? "has-value" : ""}>
        {value || placeholder}
      </span>

      <input
        ref={inputRef}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export default function Informascota() {
  const drawPetfamWatermark = async (doc) => {
    try {
      const logoBase64 = await imageUrlToBase64(petfamLogo);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const size = 120;
      const opacity = 0.08;

      const gState = doc.GState({ opacity });
      doc.setGState(gState);

      doc.addImage(
        logoBase64,
        "PNG",
        pageWidth / 2 - size / 2,
        pageHeight / 2 - size / 2,
        size,
        size
      );

      doc.setGState(new doc.GState({ opacity: 1 }));
    } catch (e) {
      console.warn("No se pudo renderizar el watermark", e);
    }
  };

  const navigate = useNavigate();
  const location = useLocation();

  const [animClass, setAnimClass] = useState("petframe-enter");
  const [isVaccOpen, setIsVaccOpen] = useState(false);

  // ✅ Modal cargando Medismart
  const [showMedismart, setShowMedismart] = useState(false);

  const petFromState = location.state?.pet || null;
  const idPetFromState = location.state?.idPet || location.state?.petId || null;

  const idPetFromQuery = useMemo(() => {
    try {
      const qs = new URLSearchParams(location.search);
      const v = qs.get("idPet") || qs.get("petId");
      return v ? Number(v) : null;
    } catch {
      return null;
    }
  }, [location.search]);

  // ✅ si vuelves desde Medismart sin state ni query, recupera la última mascota
  const idPetFromStorage = useMemo(() => {
    const idUser = getCurrentUserId();
    return readLastPetId(idUser);
  }, []);

  const resolvedPetId =
    petFromState?.idPet ||
    petFromState?.id ||
    idPetFromState ||
    idPetFromQuery ||
    idPetFromStorage ||
    null;

  const [pet, setPet] = useState(() => petFromState);
  const [loadingPet, setLoadingPet] = useState(false);
  const [petError, setPetError] = useState("");

  // ✅ Si vuelves con "Atrás" (bfcache), NO debe quedar el loader pegado
  useEffect(() => {
    const hide = () => setShowMedismart(false);

    // Back/Forward Cache: cuando el navegador "restaura" la página
    const onPageShow = (e) => {
      if (e?.persisted) hide(); // restaurado desde cache
      else hide(); // igual lo apagamos por seguridad
    };

    // Navegación atrás/adelante
    const onPopState = () => hide();

    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useEffect(() => {
    const fetchPetIfNeeded = async () => {
      if (petFromState) {
        setPet(petFromState);
        return;
      }
          if (resolvedPetId == null) return; // ✅ solo corta si es null/undefined

      const idUser = getCurrentUserId();
      if (!idUser) {
        setPetError("No encontramos tu sesión. Inicia sesión nuevamente.");
        return;
      }

      try {
        setLoadingPet(true);
        setPetError("");

        const res = await authFetch(`/pets/user/${idUser}`);
        const json = await res.json();

        const list = json?.data || [];
        const found = list.find((p) => Number(p.idPet || p.id) === Number(resolvedPetId));

        if (!found) {
          setPetError(
            `No encontramos la mascota (idPet=${resolvedPetId}). Puede que haya sido eliminada.`
          );
          return;
        }

        setPet(found);
      } catch (e) {
        console.error(e);
        setPetError("No se pudo cargar la mascota. Intenta nuevamente.");
      } finally {
        setLoadingPet(false);
      }
    };

    fetchPetIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petFromState, resolvedPetId]);

  const fallbackPet = useMemo(
    () => ({
      idPet: 0,
      nombre: "Mascota",
      tipo: "Perro",
      raza: "Golden Retriever",
      sexo: "Macho",
      peso: "10",
      nacimiento: "2020-01-01",
      foto: golden,
    }),
    []
  );

  const safePet = pet || fallbackPet;

  const safePetId =
    safePet?.idPet || safePet?.id || safePet?.id_pet || resolvedPetId || null;

  const profilePhoto =
  safePet?.profilePhotoUrl ||
  safePet?.profilePhoto ||
  safePet?.fotoUrl ||
  safePet?.foto ||
  (safePet?.profilePhotoBase64
    ? `data:image/png;base64,${String(safePet.profilePhotoBase64).trim()}`
    : null);

  const heroImg =
    profilePhoto ||
    getBreedImage(safePet?.tipo || safePet?.species, safePet?.raza || safePet?.breed) ||
    golden;

  const petBirth =
    safePet?.nacimiento || safePet?.fechaNacimiento || safePet?.birthDate || "";

  const ageLabel = useMemo(() => getAgeLabel(petBirth), [petBirth]);

  // =======================================================
  // HEADER EDITABLE (✅ SOLO PESO)
  // =======================================================
  const [headerEditing, setHeaderEditing] = useState(false);
  const [headerValues, setHeaderValues] = useState({
    sexo: safePet.sexo || safePet.sex || "",
    peso: safePet.peso || safePet.weightKg || "",
    nacimiento: petBirth || "",
  });

  useEffect(() => {
    if (!pet) return;

    const newBirth = pet?.nacimiento || pet?.fechaNacimiento || pet?.birthDate || "";

    setHeaderValues({
      sexo: pet.sexo || pet.sex || "",
      peso: pet.peso || pet.weightKg || "",
      nacimiento: newBirth || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pet]);

  const handleHeaderChange = (field, value) => {
    if (field !== "peso") return;
    const cleaned = String(value).replace(/[^\d.,]/g, "");
    setHeaderValues((prev) => ({ ...prev, peso: cleaned }));
  };

  // ✅ solo guarda peso (PATCH recomendado)
  const savePetUpdatesToBackend = async () => {
    try {
      const cleanPeso = String(headerValues.peso || "")
        .replace(/kg/gi, "")
        .replace(",", ".")
        .trim();

      const n = Number(cleanPeso);
      if (!Number.isFinite(n) || n <= 0) return;

      const idToUpdate = safePet?.idPet || safePetId;
      if (!idToUpdate) return;

      const body = { weightKg: n };

      // ✅ PATCH /pets/{id}/weight
      const patchRes = await authFetch(`/pets/${idToUpdate}/weight`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      // fallback (compatibilidad) si algo intermedio bloquea PATCH
      if (!patchRes.ok) {
        await authFetch(`/pets/${idToUpdate}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
    } catch {}
  };

  const toggleHeaderEdit = async () => {
    if (headerEditing) await savePetUpdatesToBackend();
    setHeaderEditing((prev) => !prev);
  };

  const handleClose = () => setAnimClass("petframe-exit");

  const handleAnimationEnd = () => {
    if (animClass === "petframe-exit") {
      navigate("/interfaz", { replace: true });
    }
  };

  // ✅ helper para asegurar que el modal se pinte antes de redirigir
  const nextPaint = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

  // ✅ Medismart: modal cargando hasta redirección
  const openMedismartModal = async () => {
    setShowMedismart(true);

    const idUser = getCurrentUserId();
    console.log("🧪 ID USER:", idUser);

    if (!idUser) {
      alert("No encontramos tu sesión. Inicia sesión nuevamente.");
      setShowMedismart(false);
      return;
    }

    // ✅ CLAVE: guardar la mascota REAL (evita guardar 0 del fallback)
const currentPetId =
  Number(pet?.idPet || pet?.id || pet?.id_pet || resolvedPetId || null) || null;

saveLastPetId(idUser, currentPetId);

    let redirected = false;

    try {
      // (No cambia tu backend, solo agrega idPet como info extra; si no lo usas, no pasa nada)
      const endpoint = `${API_BASE_URL}/api/medismart/sso?idUser=${idUser}&idPet=${currentPetId}`;
      console.log("🧪 Llamando a:", endpoint);

      const res = await fetch(endpoint, { method: "GET" });

      const text = await res.text();
      console.log("🧪 RAW BACKEND:", text);

      let json;
      try {
        json = JSON.parse(text);
      } catch {
        alert("El backend no devolvió JSON válido. Revisa consola.");
        return;
      }

      console.log("🧪 JSON:", json);

      const url = json?.data?.url;
      if (!url) {
        alert(json?.userMessage || "No llegó URL SSO desde backend.");
        return;
      }

      console.log("🧪 URL SSO:", url);

      await nextPaint();
      await new Promise((r) => setTimeout(r, 120));

      redirected = true;
      window.location.assign(url);
    } catch (err) {
      console.log("❌ ERROR FETCH MEDISMART:", err);
      alert("Error contactando tu backend. Intenta otra vez.");
    } finally {
      if (!redirected) setShowMedismart(false);
    }
  };

  // =======================================================
  // CARNET: cargar HECHO/VACCINE de BD
  // =======================================================
  const [carnetRows, setCarnetRows] = useState([]);
  const [loadingCarnet, setLoadingCarnet] = useState(false);
  const [carnetError, setCarnetError] = useState("");

  // ✅ debounce limpio (arregla que “no deje escribir seguido”)
  const saveTimersRef = useRef(new Map());

  const saveVeterinarianToBackend = async (idEvent, veterinarian) => {
    if (!idEvent) return;

    try {
      const res = await authFetch(`/calendar/events/${idEvent}/veterinarian`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ veterinarian }),
      });

      if (!res.ok) throw new Error(`Backend respondió ${res.status}`);
    } catch (e) {
      console.error(e);
      setCarnetError("No se pudo guardar el veterinario. Revisa tu conexión e intenta de nuevo.");
      window.setTimeout(() => setCarnetError(""), 2600);
    }
  };

  const loadCarnet = async () => {
    const idUser = getCurrentUserId();
    if (!idUser || !safePetId) return;

    try {
      setLoadingCarnet(true);
      setCarnetError("");

      const res = await authFetch(`/calendar/events?idUser=${idUser}`);
      const json = await res.json();
      const data = Array.isArray(json?.data) ? json.data : [];

      const doneForPet = data
        .filter((ev) => {
          const idPet = ev?.idPet ?? ev?.id_pet ?? null;
          const type = String(ev?.eventType ?? ev?.event_type ?? "").toUpperCase();
          return Number(idPet) === Number(safePetId) && type === "VACCINE" && isDoneStatus(ev?.status);
        })
        .map((ev) => {
          const idEvent = ev?.idEvent ?? ev?.id_event ?? ev?.id ?? null;
          const date = toISODateOnly(ev?.eventDate ?? ev?.event_date ?? "");
          const vaccine = ev?.title || "Vacuna";
          const veterinarian = ev?.veterinarian ?? "";

          return {
            id: `BD-${idEvent ?? Math.random()}`,
            date,
            vaccine,
            veterinarian,
            _bdEventId: idEvent ?? null,
            _isNew: false,
          };
        })
        .sort((a, b) => {
          const da = a.date ? new Date(a.date).getTime() : 0;
          const db = b.date ? new Date(b.date).getTime() : 0;
          return da - db;
        });

      setCarnetRows(doneForPet);
    } catch (e) {
      console.error(e);
      setCarnetError("No se pudo cargar el carnet. Intenta nuevamente.");
      setCarnetRows([]);
    } finally {
      setLoadingCarnet(false);
    }
  };

  useEffect(() => {
    loadCarnet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePetId]);

  // =======================================================
  // CARNET: + AÑADIR (editar fecha, vacuna, veterinario)
  // =======================================================
  const [savingCarnet, setSavingCarnet] = useState(false);

  const [deleteMode, setDeleteMode] = useState(false);

  const [carnetVetiaModal, setCarnetVetiaModal] = useState({
  open: false,
  saving: false,
  error: "",
  success: "",
});

const [structuredRows, setStructuredRows] = useState([
  {
    id: makeRowId(),
    vaccineType: "",
    dose: "",
    date: "",
    veterinarian: "",
  },
]);

const [showCustomVaccines, setShowCustomVaccines] = useState(false);

const [customRows, setCustomRows] = useState([
  {
    id: makeRowId(),
    name: "",
    date: "",
    veterinarian: "",
  },
]);

  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
  open: false,
  row: null,
});

const toggleDeleteMode = () => {
  setDeleteMode((prev) => !prev);
};

  const addCarnetRow = () => {
  setCarnetVetiaModal({
    open: true,
    saving: false,
    error: "",
    success: "",
  });

  setStructuredRows([
    {
      id: makeRowId(),
      vaccineType: "",
      dose: "",
      date: "",
      veterinarian: "",
    },
  ]);

  setCustomRows([
    {
      id: makeRowId(),
      name: "",
      date: "",
      veterinarian: "",
    },
  ]);

  setShowCustomVaccines(false);
};

const closeCarnetVetiaModal = () => {
  if (carnetVetiaModal.saving) return;

  setCarnetVetiaModal({
    open: false,
    saving: false,
    error: "",
    success: "",
  });
};

const addStructuredRow = () => {
  setStructuredRows((prev) => [
    ...prev,
    {
      id: makeRowId(),
      vaccineType: "",
      dose: "",
      date: "",
      veterinarian: "",
    },
  ]);
};

const updateStructuredRow = (id, field, value) => {
  setStructuredRows((prev) =>
    prev.map((r) =>
      r.id === id
        ? {
            ...r,
            [field]: field === "veterinarian" ? upper(value) : value,
          }
        : r
    )
  );
};

const removeStructuredRow = (id) => {
  setStructuredRows((prev) =>
    prev.length > 1 ? prev.filter((r) => r.id !== id) : prev
  );
};

const addCustomRow = () => {
  setCustomRows((prev) => [
    ...prev,
    {
      id: makeRowId(),
      name: "",
      date: "",
      veterinarian: "",
    },
  ]);
};

const updateCustomRow = (id, field, value) => {
  setCustomRows((prev) =>
    prev.map((r) =>
      r.id === id
        ? {
            ...r,
            [field]: field === "veterinarian" ? upper(value) : value,
          }
        : r
    )
  );
};

const removeCustomRow = (id) => {
  setCustomRows((prev) =>
    prev.length > 1 ? prev.filter((r) => r.id !== id) : prev
  );
};

const buildStructuredPayload = () => {
  return structuredRows
    .map((r) => ({
      vaccineType: r.vaccineType,
      dose: r.dose,
      date: r.date || null,
      veterinarian: (r.veterinarian || "").trim(),
    }))
    .filter((r) => r.vaccineType && r.dose && r.date);
};

const buildCustomPayload = () => {
  return customRows
    .map((r) => ({
      name: (r.name || "").trim(),
      date: r.date || null,
      veterinarian: (r.veterinarian || "").trim(),
    }))
    .filter((r) => r.name && r.date);
};

const saveCarnetVaccinesWithVetia = async () => {
  const idUser = getCurrentUserId();

  if (!idUser || !safePetId) {
    setCarnetVetiaModal((prev) => ({
      ...prev,
      error: "No pude identificar el usuario o la mascota.",
    }));
    return;
  }

  const structuredVaccines = buildStructuredPayload();
  const customVaccines = buildCustomPayload();

  if (structuredVaccines.length === 0 && customVaccines.length === 0) {
    setCarnetVetiaModal((prev) => ({
      ...prev,
      error: "Añade al menos una vacuna con fecha.",
    }));
    return;
  }

  try {
    setCarnetVetiaModal((prev) => ({
      ...prev,
      saving: true,
      error: "",
      success: "",
    }));

    const payload = {
      petId: safePetId,
      idUser,
      species: getPetSpeciesForVaccines(safePet),
      birthDate:
        safePet?.birthDate ||
        safePet?.nacimiento ||
        safePet?.fechaNacimiento ||
        "",
      vaccineStatus: "ANOTADAS",
      structuredVaccines,
      customVaccines,
    };

    const res = await authFetch(`/vet-assistant/vaccines/plan/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const json = await res.json().catch(() => null);

    if (!res.ok || !json?.data) {
      throw new Error(
        json?.userMessage || "No se pudo generar el plan de vacunación."
      );
    }

    await loadCarnet();

    setCarnetVetiaModal((prev) => ({
      ...prev,
      saving: false,
      error: "",
      success:
        "Dra. Vetia analizó las vacunas de tu mascota y generó el esquema correspondiente en el calendario.",
    }));

    window.setTimeout(() => {
  setCarnetVetiaModal({
    open: false,
    saving: false,
    error: "",
    success: "",
  });
}, 1800);
  } catch (e) {
    console.error(e);
    setCarnetVetiaModal((prev) => ({
      ...prev,
      saving: false,
      error:
        "No se pudo guardar y analizar las vacunas. Revisa la conexión o el backend.",
    }));
  }
};

  const updateCarnetField = (rowId, field, value) => {
    setCarnetRows((prev) =>
      (Array.isArray(prev) ? prev : []).map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
    );
  };

  const removeCarnetRow = (rowId) => {
    setCarnetRows((prev) => (Array.isArray(prev) ? prev : []).filter((r) => r.id !== rowId));
  };

  const deleteCarnetRowFromBackend = (row) => {
  if (!row) return;

  if (row._isNew) {
    removeCarnetRow(row.id);
    return;
  }

  setDeleteConfirmModal({
    open: true,
    row,
  });
};

const closeDeleteConfirmModal = () => {
  setDeleteConfirmModal({
    open: false,
    row: null,
  });
};

const confirmDeleteCarnetRow = async () => {
  const row = deleteConfirmModal.row;
  if (!row) return;

  const idEvent = row._bdEventId;

  if (!idEvent) {
    setCarnetError("No pude identificar esta vacuna para eliminarla.");
    window.setTimeout(() => setCarnetError(""), 2500);
    closeDeleteConfirmModal();
    return;
  }

  try {
    setSavingCarnet(true);
    setCarnetError("");

    const res = await authFetch(`/calendar/events/${idEvent}`, {
      method: "DELETE",
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Backend respondió ${res.status}`);
    }

    await loadCarnet();
    closeDeleteConfirmModal();
  } catch (e) {
    console.error(e);
    setCarnetError("No se pudo eliminar la vacuna. Revisa el backend.");
    window.setTimeout(() => setCarnetError(""), 3200);
  } finally {
    setSavingCarnet(false);
  }
};


  // 🔥 Intenta crear el evento en backend (ajústalo si tu endpoint/body es distinto)
  const createVaccineEventInBackend = async ({ idUser, idPet, date, vaccine, veterinarian }) => {
    const payload = {
      idUser,
      idPet,
      eventType: "VACCINE",
      status: "HECHO",
      eventDate: date, // "YYYY-MM-DD"
      title: vaccine,
      veterinarian: veterinarian || "",
    };

    const res = await authFetch(`/calendar/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`No se pudo crear el evento (${res.status}). ${t}`);
    }

    return await res.json().catch(() => ({}));
  };

  const saveNewCarnetRow = async (row) => {
    const idUser = getCurrentUserId();
    if (!idUser || !safePetId) return;

    const date = (row.date || "").trim();
    const vaccine = (row.vaccine || "").trim();
    const veterinarian = (row.veterinarian || "").trim();

    if (!date || !vaccine) {
      setCarnetError("Completa al menos Fecha y Vacuna.");
      window.setTimeout(() => setCarnetError(""), 2200);
      return;
    }

    try {
      setSavingCarnet(true);
      setCarnetError("");

      await createVaccineEventInBackend({
        idUser,
        idPet: safePetId,
        date,
        vaccine,
        veterinarian,
      });

      await loadCarnet();
    } catch (e) {
      console.error(e);
      setCarnetError(
        "No pude guardar la vacuna en la BD. Si tu endpoint no es POST /calendar/events, dime cuál es y lo ajusto."
      );
      window.setTimeout(() => setCarnetError(""), 3200);
    } finally {
      setSavingCarnet(false);
    }
  };

  // =======================================================
  // QR público + PDF
  // =======================================================
  const [carnetPublicUrl, setCarnetPublicUrl] = useState("");
  const [loadingPublicId, setLoadingPublicId] = useState(false);

  const buildPublicUrl = (publicId) => {
    return `${window.location.origin}/carnet/public/${publicId}`;
  };

  const loadOrCreatePublicId = async () => {
    if (!safePetId) return;

    try {
      setLoadingPublicId(true);
      const res = await authFetch(`/pets/${safePetId}/carnet/public-id`);
      const json = await res.json();
      const publicId = json?.data?.publicId;
      if (publicId) setCarnetPublicUrl(buildPublicUrl(publicId));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPublicId(false);
    }
  };

  useEffect(() => {
    if (isVaccOpen) loadOrCreatePublicId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVaccOpen, safePetId]);

  // =======================================================
  // PDF
  // =======================================================
  const downloadCarnetPdf = async () => {
    try {
      const doc = new jsPDF();

      await drawPetfamWatermark(doc);

      doc.setFillColor(0, 149, 169);
      doc.rect(0, 0, 210, 45, "F");

      const petName = safePet?.nombre || safePet?.name || "Mascota";
      const sexo = headerValues.sexo || "—";
      const peso = headerValues.peso
        ? `${String(headerValues.peso).replace(/kg/gi, "").trim()} kg`
        : "—";
      const nacimiento = headerValues.nacimiento || "—";

      doc.setFillColor(0, 149, 169);
      doc.rect(0, 0, 210, 45, "F");

      doc.setTextColor("#ffffff");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Carnet de vacunación", 14, 18);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("www.petfam.club", 14, 24);

      doc.setFontSize(11);
      doc.text(petName, 14, 30);

      if (heroImg) {
        try {
          const imgBase64 = await imageUrlToBase64(heroImg);

          const imgW = 34;
          const imgH = 34;
          const imgX = 158;
          const imgY = 6;

          const cx = imgX + imgW / 2;
          const cy = imgY + imgH / 2;
          const r = 20;

          doc.setFillColor(255, 255, 255);
          doc.circle(cx, cy, r, "F");

          doc.setDrawColor(210, 210, 210);
          doc.setLineWidth(0.8);
          doc.circle(cx, cy, r, "S");

          const isPng = String(imgBase64).startsWith("data:image/png");
          doc.addImage(imgBase64, isPng ? "PNG" : "JPEG", imgX, imgY, imgW, imgH);
        } catch (e) {
          console.warn("No se pudo cargar la imagen para el PDF", e);
        }
      }

      doc.setTextColor("#0B2F3E");
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(10, 40, 190, 22, 8, 8, "F");

      doc.setFontSize(10);
      doc.text(`Sexo: ${sexo}`, 20, 54);
      doc.text(`Peso: ${peso}`, 80, 54);
      doc.text(`Nacimiento: ${nacimiento}`, 135, 54);

      const rows = (carnetRows || []).map((r) => [r.date || "", r.vaccine || "", r.veterinarian || "—"]);

      autoTable(doc, {
        startY: 70,
        head: [["Fecha", "Vacuna", "Veterinario"]],
        body: rows.length ? rows : [["—", "Aún no hay vacunas HECHO", "—"]],
        styles: { fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [0, 149, 169], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 14, right: 14 },
      });

      doc.save(`carnet-${petName}.pdf`);
    } catch (err) {
      console.error(err);
      alert("No se pudo generar el PDF. Intenta nuevamente.");
    }
  };

  return (
    <main className={`petframe ${animClass}`} onAnimationEnd={handleAnimationEnd}>
      <button className="close-btn" onClick={handleClose}>
        <X size={18} />
      </button>

      {loadingPet && (
        <div style={{ padding: 18, color: "#0b2f3e", fontFamily: "Poppins" }}>
          Cargando información de tu mascota…
        </div>
      )}

      {petError && (
        <div style={{ padding: 18, color: "#c0392b", fontFamily: "Poppins" }}>
          {petError}
        </div>
      )}

      {!petError && (
        <>
          <input style={{ display: "none" }} type="file" accept="image/*" onChange={() => {}} />

          <div className="desktop-layout">
            <div className="kitten-card">
              <div className="card-header">
                <div className="brand">
                  <span className="brand-dot" />
                  Controles
                </div>

                <nav className="nav">
                  {headerEditing ? (
                    <>
                      <span className="navlink is-active">{headerValues.sexo || "Sexo"}</span>
                      <input
                        className="nav-input"
                        value={headerValues.peso}
                        onChange={(e) => handleHeaderChange("peso", e.target.value)}
                        inputMode="decimal"
                        placeholder="Peso (kg)"
                      />
                      <span className="navlink">{headerValues.nacimiento || "Nacimiento"}</span>
                    </>
                  ) : (
                    <>
                      <span className="navlink is-active">{headerValues.sexo || "Sexo"}</span>
                      <span className="navlink">
                        {headerValues.peso
                          ? `${String(headerValues.peso).replace(/kg/gi, "").trim()} kg`
                          : "Peso"}
                      </span>
                      <span className="navlink">{headerValues.nacimiento || "Nacimiento"}</span>
                    </>
                  )}
                </nav>
              </div>

              <div className="watermark">
                {(safePet.nombre || safePet.name || "Mascota").toUpperCase()}
              </div>

              <section className="left-col">
                <h1 className="hero-title">{safePet.raza || safePet.breed}</h1>
                <p className="subtitle">{ageLabel}</p>

                <button className="edit-btn" onClick={toggleHeaderEdit}>
                  <Pencil size={16} />
                  <span className="edit-text">{headerEditing ? "Guardar" : "Editar"}</span>
                </button>

                <span className="vline" />
              </section>

              <img
                className="hero-dog"
                src={heroImg}
                alt={safePet.nombre || safePet.name}
                decoding="async"
                fetchPriority="high"
              />
            </div>

            <section className="desktop-actions">
              <div className="appt-block">
                <h3 className="appt-title">Programar cita</h3>
                <div className="appt-actions">
                  <button className="appt-service-card" onClick={openMedismartModal}>
                    <div className="appt-service-photo">
                      <img src={etImg} alt="Consulta" loading="lazy" />
                    </div>
                    <div className="appt-service-pill">Solicitar consulta</div>
                  </button>
                </div>

                <p className="appt-note">
                  Consultas operadas en alianza con Medismart.life, expertos en atención veterinaria
                  digital.
                </p>
              </div>

              <article
                className={`vacc-card ${isVaccOpen ? "is-open" : ""}`}
                onClick={() => !isVaccOpen && setIsVaccOpen(true)}
              >
                <div className="vacc-book">
                  <div className="vacc-cover">
  <div className="vacc-cover-text">
    <span className="vacc-cover-title">CARNET</span>
    <span className="vacc-cover-subtitle">(Identificación y vacunación)</span>
  </div>

  <div className="vacc-cover-arrow">
    <ArrowRight size={18} />
  </div>
</div>

                  <div className="vacc-inner">
                    <button
                      className="vacc-close"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsVaccOpen(false);
                      }}
                    >
                      <X size={12} />
                    </button>

                    <CarnetContent
                      API_BASE_URL={API_BASE_URL}
                      carnetRows={carnetRows}
                      loadingCarnet={loadingCarnet}
                      carnetError={carnetError}
                      savingCarnet={savingCarnet}
                      addCarnetRow={addCarnetRow}
                      updateCarnetField={updateCarnetField}
                      saveNewCarnetRow={saveNewCarnetRow}
                      removeCarnetRow={removeCarnetRow}
deleteCarnetRowFromBackend={deleteCarnetRowFromBackend}
deleteMode={deleteMode}
toggleDeleteMode={toggleDeleteMode}
setCarnetRows={setCarnetRows}
                      loadingPublicId={loadingPublicId}
                      carnetPublicUrl={carnetPublicUrl}
                      downloadCarnetPdf={downloadCarnetPdf}
                    />
                  </div>
                </div>
              </article>
            </section>
          </div>

          <section className="pet-mobile">
            <div className="petm-carnet-wrapper">
              <button type="button" className="petm-carnet-btn" onClick={() => setIsVaccOpen(true)}>
                Ver carnet
              </button>
            </div>

            <div className="petm-top">
              <div style={{ cursor: "default" }}>
                <img
                  src={heroImg}
                  alt={safePet.nombre || safePet.name}
                  className="petm-dog-img"
                  decoding="async"
                />
              </div>

              <div className="petm-arc">
                <h1 className="petm-name">{safePet.nombre || safePet.name}</h1>
                <p className="petm-age">{ageLabel}</p>
              </div>
            </div>

            <div className="petm-body">
              <div className="petm-breed-row">
                <span className="petm-breed">{safePet.raza || safePet.breed}</span>

                <button className="petm-edit-btn" onClick={toggleHeaderEdit}>
                  {headerEditing ? <Check size={16} /> : <Pencil size={16} />}
                </button>
              </div>

              <div className="petm-pills">
                <div className="petm-pill">
                  <span className="pill-main">{headerValues.sexo || "Sexo"}</span>
                  <span className="pill-label">Sexo</span>
                </div>

                <div className="petm-pill">
                  {headerEditing ? (
                    <>
                      <input
                        className="pill-input"
                        value={headerValues.peso}
                        onChange={(e) => handleHeaderChange("peso", e.target.value)}
                        inputMode="decimal"
                        placeholder="Peso (kg)"
                      />
                      <span className="pill-label">Peso</span>
                    </>
                  ) : (
                    <>
                      <span className="pill-main">
                        {headerValues.peso
                          ? `${String(headerValues.peso).replace(/kg/gi, "").trim()} kg`
                          : "Peso"}
                      </span>
                      <span className="pill-label">Peso</span>
                    </>
                  )}
                </div>

                <div className="petm-pill">
                  <span className="pill-main">{headerValues.nacimiento || "Nacimiento"}</span>
                  <span className="pill-label">Nacimiento</span>
                </div>
              </div>

              <h2 className="petm-subtitle">Programar cita</h2>

              <div className="petm-services petm-services--single">
  <button className="petm-consulta-card" onClick={openMedismartModal}>
    <div className="petm-consulta-photo">
      <img src={etImg} alt="Consulta" loading="lazy" />
    </div>
    <div className="petm-consulta-pill">Solicitar consulta</div>
  </button>
</div>

<p className="petm-appt-note">
  Consultas operadas en alianza con Medismart.life, expertos en atención veterinaria digital.
</p>
            </div>

            {isVaccOpen && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,.45)",
                  backdropFilter: "blur(8px)",
                  zIndex: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 15,
                }}
                onClick={() => setIsVaccOpen(false)}
              >
                <div
                  style={{
                    width: "min(980px, 96vw)",
                    maxHeight: "86vh",
                    background: "#fff",
                    borderRadius: 22,
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                    boxShadow: "0 20px 60px rgba(0,0,0,.35)",
                    position: "relative",
                    padding: 16,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setIsVaccOpen(false)}
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      width: 34,
                      height: 34,
                      borderRadius: 999,
                      border: "none",
                      background: "#fff",
                      boxShadow: "0 10px 26px rgba(0,0,0,.18)",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      color: "#0B2F3E",
                      zIndex: 5,
                    }}
                  >
                    <X size={14} />
                  </button>

                  <div>
                    <CarnetContent
                      API_BASE_URL={API_BASE_URL}
                      carnetRows={carnetRows}
                      loadingCarnet={loadingCarnet}
                      carnetError={carnetError}
                      savingCarnet={savingCarnet}
                      addCarnetRow={addCarnetRow}
                      updateCarnetField={updateCarnetField}
                      saveNewCarnetRow={saveNewCarnetRow}
                      removeCarnetRow={removeCarnetRow}
deleteCarnetRowFromBackend={deleteCarnetRowFromBackend}
deleteMode={deleteMode}
toggleDeleteMode={toggleDeleteMode}
setCarnetRows={setCarnetRows}
                      loadingPublicId={loadingPublicId}
                      carnetPublicUrl={carnetPublicUrl}
                      downloadCarnetPdf={downloadCarnetPdf}
                    />
                  </div>
                </div>
              </div>
            )}
          </section>

          {false && (
            <div className="cropper-overlay">
              <div className="cropper-modal">
                <div className="cropper-area">
                  <Cropper image={null} crop={{ x: 0, y: 0 }} zoom={1} aspect={1} />
                </div>
              </div>
            </div>
          )}

          {deleteConfirmModal.open &&
  createPortal(
    <div className="pf-delete-overlay" onClick={closeDeleteConfirmModal}>
      <div className="pf-delete-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="pf-delete-close"
          onClick={closeDeleteConfirmModal}
        >
          <X size={18} />
        </button>

        <div className="pf-delete-icon">
          <Trash2 size={26} />
        </div>

        <h3>Eliminar vacuna</h3>

        <p>¿Seguro que quieres eliminar esta vacuna del carnet?</p>

        <div className="pf-delete-vaccine">
          <strong>{deleteConfirmModal.row?.vaccine || "Vacuna"}</strong>
          <span>{deleteConfirmModal.row?.date || ""}</span>
        </div>

        <div className="pf-delete-actions">
          <button type="button" onClick={closeDeleteConfirmModal}>
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmDeleteCarnetRow}
            disabled={savingCarnet}
          >
            {savingCarnet ? "Eliminando..." : "Sí, eliminar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

  {carnetVetiaModal.open &&
  createPortal(
    <div className="pf-delete-overlay" onClick={closeCarnetVetiaModal}>
      <div
        className="pf-delete-modal carnet-vetia-modal"
        style={{ width: "min(760px, 94vw)", maxHeight: "85vh", overflowY: "auto", WebkitOverflowScrolling: "touch", }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="pf-delete-close"
          onClick={closeCarnetVetiaModal}
          disabled={carnetVetiaModal.saving}
        >
          <X size={18} />
        </button>

        <div className="pf-delete-icon" style={{ background: "#0095a9", color: "white" }}>
          <Syringe size={26} />
        </div>

        <h3>Añadir vacunas al carnet</h3>

        <p>
          Registra las vacunas aplicadas.
        </p>

        <div style={{ marginTop: 14, textAlign: "left" }}>
          <h4 style={{ color: "#0B2F3E", marginBottom: 10 }}>
            Vacunas del esquema
          </h4>

          <div className="vet-items">
            {structuredRows.map((row) => (
              <div key={row.id} className="vet-structured-row">
                <select
  style={carnetInputStyle}
  value={row.vaccineType}
  onChange={(e) =>
    updateStructuredRow(row.id, "vaccineType", e.target.value)
  }
>
                  <option value="">Tipo de vacuna</option>

                  {getPetSpeciesForVaccines(safePet) === "PERRO" ? (
                    <>
                      <option value="POLIVALENTE_CANINA">Polivalente canina</option>
                      <option value="RABIA">Rabia</option>
                    </>
                  ) : (
                    <>
                      <option value="TRIPLE_FELINA">Triple felina</option>
                      <option value="RABIA">Rabia</option>
                    </>
                  )}
                </select>

                <select
  style={carnetInputStyle}
  value={row.dose}
  onChange={(e) =>
    updateStructuredRow(row.id, "dose", e.target.value)
  }
>
                  <option value="">Dosis</option>
                  <option value="FIRST">1ª dosis</option>
                  <option value="SECOND">2ª dosis</option>
                  <option value="THIRD">3ª dosis</option>
                  <option value="ANNUAL">Refuerzo anual</option>
                </select>

<CarnetDateField
  value={row.date}
  onChange={(value) => updateStructuredRow(row.id, "date", value)}
/>

                <input
  style={carnetInputStyle}
  value={row.veterinarian}
  onChange={(e) =>
    updateStructuredRow(row.id, "veterinarian", e.target.value)
  }
  placeholder="Veterinario"
/>

                <button
                  type="button"
                  className="vet-icon-btn"
                  onClick={() => removeStructuredRow(row.id)}
                  disabled={structuredRows.length === 1}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>

          <button
  type="button"
  onClick={addStructuredRow}
  style={{
    marginTop: 10,
    background: "transparent",
    border: "none",
    color: "#0F6B7A",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  }}
>
  <Plus size={14} />
  Añadir vacuna
</button>

          <button
            type="button"
            onClick={() => setShowCustomVaccines((s) => !s)}
            className="vet-link-btn"
          >
            <Plus size={14} />
            Añadir otra vacuna diferente
          </button>

          {showCustomVaccines && (
            <div className="custom-vaccines-wrap">
              <h4 style={{ color: "#0B2F3E", margin: "12px 0 10px" }}>
                Otras vacunas
              </h4>

              {customRows.map((row) => (
                <div key={row.id} className="vet-structured-row">
                  <input
                    value={row.name}
                    onChange={(e) =>
                      updateCustomRow(row.id, "name", e.target.value)
                    }
                    placeholder="Nombre de la vacuna"
                  />

                  <div />

                  <CarnetDateField
  value={row.date}
  onChange={(value) => updateCustomRow(row.id, "date", value)}
/>

                  <input
                    value={row.veterinarian}
                    onChange={(e) =>
                      updateCustomRow(row.id, "veterinarian", e.target.value)
                    }
                    placeholder="Veterinario"
                  />

                  <button
                    type="button"
                    className="vet-icon-btn"
                    onClick={() => removeCustomRow(row.id)}
                    disabled={customRows.length === 1}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}

              <button type="button" onClick={addCustomRow} className="vet-add-btn">
                <Plus size={16} /> Añadir otra vacuna diferente
              </button>
            </div>
          )}
        </div>

        {carnetVetiaModal.error && (
          <p style={{ color: "#C0392B", fontWeight: 700, marginTop: 12 }}>
            {carnetVetiaModal.error}
          </p>
        )}

        {carnetVetiaModal.success && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 16,
              background: "#E9F8F1",
              color: "#116B45",
              fontWeight: 700,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <ShieldCheck size={18} />
            {carnetVetiaModal.success}
          </div>
        )}

        <div className="pf-delete-actions">
          <button
            type="button"
            onClick={closeCarnetVetiaModal}
            disabled={carnetVetiaModal.saving}
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={saveCarnetVaccinesWithVetia}
            disabled={carnetVetiaModal.saving}
          >
            {carnetVetiaModal.saving
              ? "Analizando..."
              : "Guardar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

          {/* ✅ MODAL CARGANDO MEDISMART */}
          {showMedismart &&
            createPortal(
              <div className="ms-overlay">
                <div className="ms-loader-box">
                  <div className="ms-spinner"></div>
                  <p>Estamos preparando tu consulta con Medismart.life...</p>
                </div>
              </div>,
              document.body
            )}
        </>
      )}
    </main>
  );
}